import styled from 'styled-components';

/**
 * Creates a new themeable component for you. See the [theming](#theming) section
 * for more information about how theming works.
 *
 * @public
 * @author Connor Taylor
 *
 * @param { Object } options the options to configure the theming
 * @param { string } options.themeKey The key that this component uses to find
 *   theme styles in a theme object. For example, if this is set to `Button`,
 *   it will look for theme styles under theme.Button
 * @param { string | ReactComponent } options.component The styled component to
 *   use as a base. It can be a string if it's a plain DOM element (eg. div) or
 *   a React Component if you want to make an existing component themeable
 * @param {Array} options.defaultStyles The default styles to apply to the
 *   component. Must be the result of calling the `css` helper function from
 *   styled-components
 * @param {String} [options.displayName=themeKey] The React displayName for the
 *   component. Defaults to using the themeKey if not provided
 * @return {StyledComponent} A new styled component for you to use
 *
 * @example
 * const Button = makeThemedComponent({
 *   themeKey: `Button`,
 *   component: `button`,
 *   defaultStyles: css`background: #FF0000;`,
 *   displayName: `MyLittleButton`,
 * });
 *
 * const WrappedButton = makeThemedComponent({
 *   themeKey: `Button`, // Same theme key === same theme styles
 *   component: Button,
 *   defaultStyles: css`background: #0f0;`,
 * });
 */
const makeThemedComponent = ({
  themeKey,
  component,
  defaultStyles,
  displayName = ``,
}) => {
  const C = styled(component)`
    ${(props) => {
    // Guard against no theme
    const themeStyles
        = props.theme && props.theme[themeKey] ? props.theme[themeKey] : [];

    return [...defaultStyles, ...themeStyles];
  }}
  `;
  C.displayName = displayName || themeKey;

  return C;
};

/** @component */
export default makeThemedComponent;
